import { Suspense, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { chain, deployment, isLocalChain } from '../../config/chain';
import { useChainClock } from '../../hooks/chainClock';
import { PixelIcon } from '../pixel/icons';
import { WalletButton } from './WalletButton';
import s from './AppShell.module.css';

const NAV = [
  { to: '/launches', label: 'Launches' },
  { to: '/create', label: 'Launch a token' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/how', label: 'How it works' },
  { to: '/docs', label: 'Docs' },
  { to: '/lab', label: 'Lab' },
];

export function AppShell() {
  const { pathname } = useLocation();
  const clock = useChainClock();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className={s.shell}>
      <a href="#main" className={s.skip}>
        Skip to content
      </a>
      <header className={s.header}>
        <div className={s.headerInner}>
          <Link to="/" className={s.wordmark} aria-label="HASHCURVE home">
            <PixelIcon name="anvil" px={2} />
            <span>
              HASH<span className={s.curve}>CURVE</span>
            </span>
          </Link>
          <nav className={s.nav} aria-label="Main">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? `${s.navLink} ${s.active}` : s.navLink)}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className={s.right}>
            <span className={s.ticker} title={`${chain.name} (chain id ${chain.id}); latest block seen by this page`}>
              <span className={clock.rpcError ? s.dotBad : s.dot} aria-hidden="true" />
              <span className={s.tickerChain}>{isLocalChain ? 'LOCAL' : chain.id === 4663 ? 'ROBINHOOD' : chain.name}</span>
              <span className={s.tickerSep} aria-hidden="true" />
              <span className={s.tickerBlock}>{clock.block ? `#${clock.block.number.toLocaleString('en-US')}` : '—'}</span>
            </span>
            <WalletButton />
          </div>
        </div>
      </header>
      {clock.rpcError ? (
        <div className={s.banner} role="status">
          Cannot reach the chain RPC. Live state is paused until it comes back.
        </div>
      ) : null}
      {!deployment ? (
        <div className={s.banner} role="status">
          HASHCURVE has no deployment on {chain.name} ({chain.id}) in this build. Reading and trading are unavailable.
        </div>
      ) : null}
      <main id="main" className={s.main}>
        <Suspense fallback={<div className="page muted">Loading…</div>}>
          <Outlet />
        </Suspense>
      </main>
      <footer className={s.footer}>
        <div className={s.footerInner}>
          <div className={s.footBrand}>
            <PixelIcon name="flame" px={2} />
            <p className={s.risk}>Tokens launched here can lose all value. Nothing here is investment advice. Mining uses electricity.</p>
          </div>
          <nav className={s.footNav} aria-label="Footer">
            <Link to="/terms">Terms</Link>
            <Link to="/docs">Docs</Link>
            <Link to="/how">How it works</Link>
            <Link to="/how#hashrate">Estimated hashrate method</Link>
            <Link to="/lab">Miner lab</Link>
          </nav>
          <p className={s.fine}>Every scene on this site animates from live chain state.</p>
        </div>
      </footer>
    </div>
  );
}
